import logger from '../utils/logger';

export const COLLECTIONS = {
  CLIENTS: 'clients',
  // CLIENTHISTORY removed in PR 4: the legacy top-level `clientHistory`
  // collection had no writers and only dead-code readers; canonical client
  // history lives in the `clients/{clientId}/history` subcollection (see
  // SUBCOLLECTIONS.HISTORY and addClientHistoryEntry in historyUtils.js).
  USERS: 'users',
  ADMIN_USERS: 'adminUsers',
  ENROLLMENTS: 'enrollments',
  STAFFUSERS: 'staffUsers',
  LOCATIONS: 'locations',
  LOCATION_HISTORY: 'locationHistory',
  // TRANSFERS removed in PR 6: legacy top-level `transfers` / `clientTransfers`
  // had no readers; canonical writes now go to the
  // `clients/{clientId}/transfers` subcollection (see SUBCOLLECTIONS.TRANSFERS).
  // EXITS removed in PR 5: the legacy top-level `exits` collection had no
  // active writers and stats reads have been migrated to a collectionGroup
  // query over the canonical `clients/{clientId}/exits` subcollection (see
  // SUBCOLLECTIONS.EXITS). Legacy data was reconciled by a one-time backfill.
  DAILY_OCCUPANCY: 'dailyOccupancy',
  EDDY_ROOM_OCCUPANCY: 'eddyRoomOccupancy'
};

// CLIENT_EXITS_COLLECTION removed in PR 5: legacy top-level `clientExits`
// collection has been retired. Use `clients/{clientId}/exits` (subcollection)
// or a collectionGroup('exits') query for cross-client reads.

// Convenience named exports kept for parity with legacy imports. Prefer the
// COLLECTIONS map; these direct names exist so the deprecated
// `src/utils/constants.js` shim can re-export them in one place.
export const CLIENTS_COLLECTION = 'clients';
export const LOCATION_HISTORY_COLLECTION = 'locationHistory';
// TRANSFERS_COLLECTION removed in PR 6 — see comment on COLLECTIONS above.

export const GUEST_EMAIL_DOMAIN = 'guest.communityforwardsf.org';
export const GUEST_MIN_PASSWORD_LENGTH = 12;

/** Client site / program location (not the MHOP staff role). */
export const BEHAVIORAL_HEALTH_SITE_LOCATION = 'Behavioral Health';

export const LOCATION_OPTIONS = [
  'AWP',
  'MRSC',
  'Drop-In',
  'Women\'s Flex',
  BEHAVIORAL_HEALTH_SITE_LOCATION,
  'Coronado',
  'Eddy',
  'ETH',
  'WRC'
];

/** Site options for operations contracts (includes org-wide Admin). */
export const CONTRACT_SITE_OPTIONS = ['Admin', ...LOCATION_OPTIONS];

/** Portal sites that have website training guides. */
export const WEBSITE_TRAINING_SITES = [
  { location: 'AWP', routePrefixes: ['/awp', '/awp-beds'], primaryRoute: '/awp' },
  { location: 'MRSC', routePrefixes: ['/mrsc'], primaryRoute: '/mrsc' },
  { location: 'Drop-In', routePrefixes: ['/drop-in'], primaryRoute: '/drop-in' },
  { location: 'Coronado', routePrefixes: ['/coronado'], primaryRoute: '/coronado' },
  { location: 'Eddy', routePrefixes: ['/eddy'], primaryRoute: '/eddy' },
  { location: 'ETH', routePrefixes: ['/eth'], primaryRoute: '/eth' },
  { location: 'Women\'s Flex', routePrefixes: ['/womens-flex'], primaryRoute: '/womens-flex' },
  { location: 'MHOP', routePrefixes: ['/mhop', '/behavioral-health'], primaryRoute: '/mhop', kind: 'program' }
];

export const CLIENT_PROFILE_TRAINING_LOCATION = 'Client Profile';

/** Fixed Firestore client used for the Client Profile training tour (not a real client). */
export const CLIENT_PROFILE_TRAINING_TEST_CLIENT_ID = 'cfsf-website-training-demo';

export const WEBSITE_TRAINING_LOCATIONS = [
  ...WEBSITE_TRAINING_SITES.map((s) => s.location),
  CLIENT_PROFILE_TRAINING_LOCATION
];

/** Resolve training context from pathname (portal site or training demo client profile). */
export const getTrainingContextFromPathname = (pathname) => {
  const siteLocation = getLocationForPathname(pathname);
  if (siteLocation) {
    return { location: siteLocation, kind: 'site' };
  }
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  const normalized = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
  const clientMatch = normalized.match(/^\/client\/([^/]+)$/);
  if (clientMatch && clientMatch[1] === CLIENT_PROFILE_TRAINING_TEST_CLIENT_ID) {
    return {
      location: CLIENT_PROFILE_TRAINING_LOCATION,
      kind: 'client-profile',
      testClientId: CLIENT_PROFILE_TRAINING_TEST_CLIENT_ID
    };
  }
  return null;
};

/** Resolve a portal location from the current pathname, or null if not a site route. */
export const getLocationForPathname = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  const normalized = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
  const match = WEBSITE_TRAINING_SITES.find((site) =>
    site.routePrefixes.some(
      (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
    )
  );
  return match?.location || null;
};

/** Primary portal route for a website training location. */
export const getPrimaryRouteForLocation = (location) => {
  if (location === CLIENT_PROFILE_TRAINING_LOCATION) {
    return `/client/${CLIENT_PROFILE_TRAINING_TEST_CLIENT_ID}`;
  }
  const site = WEBSITE_TRAINING_SITES.find((s) => s.location === location);
  return site?.primaryRoute || null;
};

/** Display labels for user roles (invitations, tables, etc.) */
export const ROLE_DISPLAY_LABELS = {
  admin: 'Admin',
  director: 'Director',
  manager: 'Manager',
  programManager: 'Program Manager',
  staff: 'Staff',
  mhop: 'MHOP',
  behavioralHealth: 'MHOP',
  behavioral_health: 'MHOP',
  caseManagerStaff: 'Case Manager Staff',
  hr: 'Human Resources',
  security: 'Security',
  compliance: 'Compliance',
  executive: 'Executive',
  operations: 'Operations (Receipts)',
  gss: 'GSS',
  relief: 'Relief',
  reliefManager: 'Relief Manager',
  development: 'Development',
  housingNavigator: 'Housing Navigator'
};

export const EXIT_REASONS = ['Housed', 'Left Program', 'Banned', 'Transferred', 'Other'];

/** Drop-In exit reasons: value is stored; label is shown in the UI. */
export const DROP_IN_EXIT_REASONS = [
  { value: 'Housed', label: 'Housed (sheltered, temporary housing, etc.)' },
  { value: 'Left Program', label: 'Left Program' },
  { value: 'Banned', label: 'Banned' },
  { value: 'Transferred', label: 'Transferred' },
  { value: 'Other', label: 'Other' }
];

export function getDropInExitReasonLabel(reason) {
  const key = String(reason || '').trim();
  if (!key) {
    return '';
  }
  const found = DROP_IN_EXIT_REASONS.find((r) => r.value === key);
  return found?.label || key;
}

/** Count exits filed as Housed (including legacy longer labels). */
export function countHousedExitsByReason(exitsByReason = {}) {
  if (!exitsByReason || typeof exitsByReason !== 'object') {
    return 0;
  }
  return Object.entries(exitsByReason).reduce((sum, [reason, count]) => {
    const key = String(reason || '').trim();
    const n = typeof count === 'number' ? count : 0;
    if (key === 'Housed' || key.startsWith('Housed (')) {
      return sum + n;
    }
    return sum;
  }, 0);
}

export const DIETARY_RESTRICTION_OPTIONS = [
  'Vegetarian',
  'Vegan',
  'Halal',
  'Kosher',
  'Gluten-free',
  'No dairy',
  'Nut allergy',
  'Shellfish allergy',
  'Low sodium',
  'Diabetic / low sugar'
];

export const STORAGE_PATHS = {
  CLIENT_PHOTOS: 'clientPhotos',
  CLIENT_FILES: 'clients',
  USER_AVATARS: 'avatars',
  LOCATION_IMAGES: 'locations'
};

// Normalize API_URL by removing any trailing slashes
// This handles cases where the environment variable might have a trailing slash
const getApiUrl = () => {
  let envUrl = process.env.REACT_APP_API_URL;
  const defaultUrl = 'https://api-imps3uifsa-uc.a.run.app';

  // Convert to string and trim any whitespace or trailing slashes
  if (envUrl) {
    envUrl = String(envUrl).trim().replace(/\/+$/, '');
  }

  const url = envUrl || defaultUrl;
  // Always remove trailing slashes to prevent double slashes in URLs (redundant but safe)
  return url.replace(/\/+$/, '');
};

export const API_URL = getApiUrl();

// DEBUG: Log API URL at module load
if (typeof window !== 'undefined') {
  logger.debug('constants/constants.js: API_URL =', API_URL);
  logger.debug('constants/constants.js: process.env.REACT_APP_API_URL =', process.env.REACT_APP_API_URL);
}

export const RACIAL_DEMOGRAPHIC_OPTIONS = [
  'American Indian, Alaska Native, or Indigenous',
  'Asian or Asian American',
  'Black, African American, or African',
  'Hispanic/Latina/E/O',
  'Middle Eastern or North African',
  'Native Hawaiian or Pacific Islander',
  'White',
  'Multi-Racial',
  'Declined to Answer'
];

export const GENDER_DEMOGRAPHIC_OPTIONS = [
  'Male',
  'Female',
  'Culturally Specific Gender (Two-Spirit)',
  'Transgender',
  'Transgender Female',
  'Transgender Male',
  'Non-Binary',
  'Declined to Answer'
];

export const FILE_ICONS = {
  image: 'file-image',
  pdf: 'file-pdf',
  word: 'file-word',
  excel: 'file-excel',
  video: 'file-video',
  audio: 'file-audio',
  archive: 'file-archive',
  text: 'file-text',
  default: 'file'
};

export const IT_REQUEST_ASANA_FORM_KEY = '1NGYpwS2TKaOeiqh_feNXw';
export const FACILITIES_REQUEST_ASANA_FORM_KEY = 'k7rYHihmIvEVZbT0kXDbNg';

export const HOUSING_SITE_TO_ASANA_SITE = {
  Coronado: 'The Coronado (373 Ellis Street)',
  Eddy: 'Eddy Street Apartments  (425 Eddy Street)'
};

export const quickLinks = [
  {
    title: 'Operations Manual',
    description: 'Access the complete operations guide',
    url: 'https://docs.google.com/document/d/1rSQ8dXHa2Axo9lTQQ8Bm11lp-IIiMm-Ahq3219GILbc/edit?usp=sharing',
    variant: 'info'
  },
  {
    title: 'Incident Reports',
    description: 'Submit an incident report',
    url: '/incident-reports',
    variant: 'warning',
    internal: true
  },
  {
    title: 'Facilities Requests',
    description: 'Submit a Facilities Request',
    url: '/facilities-requests',
    variant: 'success',
    internal: true
  },
  {
    title: 'Paycor',
    description: 'Access Payroll and HR resources',
    url: 'https://hcm.paycor.com/authentication/signin',
    variant: 'danger'
  }
];

export const BH_MEDI_CAL_COVERAGE_OPTIONS = [
  'Yes',
  'No',
  'App in process',
  'out of county - transfer in process',
  'out of county - declined transfer',
  'out of county - plan needed'
];

export const BH_CURRENT_PROGRAM_OPTIONS = [
  'MHOP',
  'Drop-In BH',
  'AWP BH',
  'MRSC BH',
  'Women\'s Flex BH'
];

export const SUBCOLLECTIONS = {
  HISTORY: 'history',
  NOTES: 'notes',
  EXITS: 'exits',
  /** Per-client program ban records (Drop-In checkout and future ban flows). */
  BANS: 'bans',
  /** Per-client transfer audit trail (PR 6). */
  TRANSFERS: 'transfers',
  REFERRALS: 'referrals',
  INCIDENTS: 'incidents',
  ASSESSMENTS: 'assessments',
  DOCUMENTS: 'documents',
  CONTACTS: 'contacts',
  CONFIDENTIAL_NOTES: 'confidentialNotes',
  CASE_MANAGER_NOTES: 'caseManagerNotes',
  /** Month-keyed late-rent follow-up visible to property staff. Not the confidential note. */
  RENT_FOLLOW_UPS: 'rentFollowUps',
  /** Append-only log of Drop-In queue check-ins (see `dropInArrivals.js`). */
  DROP_IN_ARRIVALS: 'dropInArrivals',
  /** Append-only log of MRSC check-ins (see `mrscArrivals.js`). */
  MRSC_ARRIVALS: 'mrscArrivals'
};

// Inactivity timeout configuration (in milliseconds)
export const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes
export const INACTIVITY_WARNING_TIME = 30 * 1000; // 30 seconds before logout
